import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import banner1 from "./img/masonry16.jpg";
import banner2 from "./img/audio-banner.webp";
import banner3 from "./img/banner-3.jpg";
import banner4 from "./img/banner4.webp";
import canonr5c from "./img/canon-r5c.jpg";
import canonr6mark2 from "./img/canon-r6-mark2.jpeg";
import shure from './img/shure.jpg'
import rodecasterPro from './img/rodecaster-pro.jpg'
import rodecasterVideo from './img/rodecaster-video.webp'
import amaran200d from './img/amaran-200d.jpg'
import amaran100x from './img/amaran-100x.jpeg'
import nanlite from './img/nanlite-rgb-tubes.jpeg'
import livestream from './img/livestream.jpg'

const categories = [
  {
    name: "Видео",
    image: banner1,
    description:
      "3 професионални камери за различни ъгли – от широк кадър, който обхваща цялата сцена, до индивидуални близки планове на всеки участник.",
    tech: [
      { name: "Canon R5C", image: canonr5c },
      { name: "Canon R6 Mark II", image: canonr6mark2 },
    ],
  },
  {
    name: "Аудио",
    image: banner2,
    description:
      "Висок клас микрофони и професионална аудио обработка за чист, плътен и равномерен звук. Използвани във водещи подкаст студиа по света. Всеки глас се записва на отделен канал за максимален контрол в постпродукция.",
    tech: [{ name: "Shure SM7B", image: shure }],
  },
  {
    name: "Конзоли",
    image: banner3,
    description:
      "Видео и аудио конзоли за пълен контрол върху продукцията в реално време. Получаваш готов епизод веднага след записа.",
    tech: [
      {
        name: "RØDECaster Pro II – аудио запис с отделни канали",
        image: rodecasterPro,
      },
      {
        name: "RØDECaster Video – видео превключване в реално време",
        image: rodecasterVideo,
      },
      { name: "Възможност за 4K Livestream", image: livestream },
    ],
  },
  {
    name: "Осветление",
    image: banner4,
    description:
      "Контролирано, кинематографично осветление за перфектен кадър – независимо от стила на видеото.",
    tech: [
      { name: "2x Amaran 200D", image: amaran200d },
      { name: "1x Amaran 100x Bi-Color", image: amaran100x },
      { name: "2x Nanlite RGB Tubes", image: nanlite },
    ],
  },
];

export default function TechStack() {
  const [emblaRef, embla] = useEmblaCarousel({ loop: false, watchDrag: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [pillStyle, setPillStyle] = useState({ x: 0, width: 0 });
  const buttonsRef = useRef([]);

  useEffect(() => {
    if (buttonsRef.current[selectedIndex]) {
      const { offsetLeft, offsetWidth } = buttonsRef.current[selectedIndex];
      setPillStyle({ x: offsetLeft, width: offsetWidth });
    }
  }, [selectedIndex]);

  const scrollTo = useCallback(
    (index) => {
      if (embla) embla.scrollTo(index);
    },
    [embla]
  );

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
  }, [embla]);

  return (
    <div className="flex flex-col items-center justify-center w-full">
      {/* Slider */}
      <div
        className="embla relative w-[90%] md:w-[80%] rounded-xl overflow-hidden md:overflow-visible"
        ref={emblaRef}
      >
        <div className="flex">
          {categories.map((category, index) => {
            const isActive = selectedIndex === index;

            return (
              <motion.div
                className={`rounded-xl relative min-w-full md:min-w-[90%] mx-auto h-[400px] md:h-[600px] flex flex-col pb-8 items-center gap-2 justify-end transition-all duration-500
              ${isActive ? "md:mx-8" : ""}`}
                initial={{ opacity: 0.5 }}
                animate={{ opacity: selectedIndex === index ? 1 : 0.5 }}
                transition={{ ease: [0.25, 1, 0.5, 1], duration: 0.7 }}
                style={{
                  backgroundImage: `url(${category.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {/* Black overlay */}
                <div className="absolute inset-0 bg-black opacity-50 rounded-xl"></div>

                <h3
                  className="text-white text-2xl md:text-3xl font-bold relative z-10"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {category.name}
                </h3>
                <p className="text-center text-sm md:text-base w-4/5 text-white mb-2 relative z-10">
                  {category.description}
                </p>
                <div className="flex flex-col w-4/5 md:flex-row items-center flex-wrap justify-center gap-4 relative z-10">
                  {category.tech.map((item) => {
                    return (
                      <div
                        key={item.name}
                        className="flex items-center gap-2"
                      >
                        <div className="w-12 h-12 p-1 flex-shrink-0 bg-white rounded-lg">
                          <img
                            className="w-full h-full object-cover rounded-lg"
                            src={item.image}
                            alt={item.name}
                          />
                        </div>
                        <h4 className='text-white text-base font-semibold w-[calc(100%-48px)]'>
                          {item.name}
                        </h4>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Category Control Menu */}
      <div className="relative mt-4 flex bg-secondary border border-border p-1 rounded-full w-fit">
        {/* Animated pill */}
        <motion.div
          className="absolute top-0 left-0 h-full bg-black rounded-full"
          animate={pillStyle}
          transition={{ ease: [0.25, 1, 0.5, 1], duration: 0.7 }}
        />

        {/* Category Buttons */}
        {categories.map((category, index) => (
          <button
            key={index}
            ref={(el) => (buttonsRef.current[index] = el)}
            onClick={() => scrollTo(index)}
            className={`relative z-10 px-3 md:px-6 py-2 text-sm md:text-base font-medium transition-colors ${
              selectedIndex === index ? "text-white" : "text-black"
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>
    </div>
  );
}
